<template>
  <container>
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Medal</h1>
        <p class="pg-title-sub">Review runner medal counts by period and medal type</p>
      </div>
    </div>

    <div class="med-card">
      <div class="med-header">
        <span class="med-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="6"/>
            <path d="M8.21 13.89 7 23l5-3 5 3-1.21-9.11"/>
            <path d="m9.5 8 1.7 1.7L15 6"/>
          </svg>
        </span>
        <div class="med-header-text">
          <h2 class="med-title">Medal Leaderboard</h2>
          <p class="med-subtitle">Filter medal totals across gold, silver, bronze and live achievements</p>
        </div>
        <div class="med-summary">
          <span class="med-summary-label">Rows</span>
          <strong>{{ medalsLoading ? '...' : tableData.length }}</strong>
        </div>
      </div>

      <div class="med-filter-grid">
        <label class="med-field">
          <span class="med-label">Year</span>
          <span class="med-select-wrap">
            <select v-model="selectedYear" name="type" class="med-select" @change="yearChangeHandle">
              <option>2018</option>
              <option>2019</option>
              <option>AllTime</option>
            </select>
            <svg class="med-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </span>
        </label>

        <label class="med-field">
          <span class="med-label">Month</span>
          <span class="med-select-wrap" :class="{ 'med-select-wrap--disabled': isAllTime }">
            <select v-model="selectedMonth" name="type" class="med-select" :disabled="isAllTime" @change="monthChangeHandle">
              <option v-for="month in filteredmonths" :key="month" :value="month">{{ month }}</option>
            </select>
            <svg class="med-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </span>
        </label>

        <label class="med-field med-field--wide">
          <span class="med-label">Ranking</span>
          <span class="med-select-wrap">
            <select v-model="selectedMedal" name="type" class="med-select" @change="medalChangeHandle">
              <option value="# of Medals"># of Medals</option>
              <option value="gold"># of Gold Medals</option>
              <option value="silver"># of Silver Medals</option>
              <option value="bronze"># of Bronze Medals</option>
              <option value="live"># of Live Medals</option>
            </select>
            <svg class="med-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </span>
        </label>
      </div>

      <div class="med-status-row">
        <span class="med-badge">{{ activePeriodLabel }}</span>
        <span class="med-badge med-badge--soft">{{ selectedMedalLabel }}</span>
      </div>

      <div v-if="medalsLoading" class="med-skeleton-table">
        <div class="med-sk-head">
          <div v-for="n in 7" :key="'head-' + n" class="med-sk-cell sk-shimmer"></div>
        </div>
        <div v-for="row in 6" :key="'row-' + row" class="med-sk-row">
          <div v-for="col in 7" :key="'row-' + row + '-col-' + col" class="med-sk-cell sk-shimmer"></div>
        </div>
      </div>

      <div v-else-if="tableData.length" class="med-table-wrap">
        <table class="med-table">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.field">
                {{ column.title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tableData" :key="row.id">
              <td v-for="column in columns" :key="column.field">
                <a v-if="column.field === 'id'" href="" class="med-runner-link" @click.stop.prevent="openRunnerData(row.id)">
                  {{ row.id }}
                </a>
                <span v-else>{{ row[column.field] }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="med-empty">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="8" r="6"/>
          <path d="M8.21 13.89 7 23l5-3 5 3-1.21-9.11"/>
        </svg>
        <p>No medal data found for this filter.</p>
      </div>

      <div class="med-pagination">
        <v-pagination
          @page-change="pageChange"
          @page-size-change="pageSizeChange"
          :total="pageTotal"
          :page-size="pageSize"
          :layout="['total', 'prev', 'pager', 'next', 'sizer', 'jumper']"
        ></v-pagination>
      </div>
    </div>
  </container>
</template>

<script>
import Container from '@/components/Container'
import 'vue-easytable/libs/themes-base/index.css'
import { VPagination } from 'vue-easytable'
import Vue from 'vue'
import MedalService from '@/services/medals'

Vue.component(VPagination.name, VPagination)

export default {
  name: 'medal',
  data () {
    return {
      tableData: [],
      columns: [
        { field: 'name', title: 'Name', width: 300, titleAlign: 'center', columnAlign: 'center', isResize: true },
        { field: 'id', title: 'ID', width: 130, titleAlign: 'center', columnAlign: 'center', componentName: 'runner-id' },
        { field: 'totalcount', title: '#', width: 130, titleAlign: 'center', columnAlign: 'center' },
        { field: 'goldcount', title: 'Gold', width: 130, titleAlign: 'center', columnAlign: 'center' },
        { field: 'silvercount', title: 'Silver', width: 130, titleAlign: 'center', columnAlign: 'center' },
        { field: 'bronzecount', title: 'Bronze', width: 130, titleAlign: 'center', columnAlign: 'center' },
        { field: 'livecount', title: 'Live', width: 130, titleAlign: 'center', columnAlign: 'center' }
      ],
      runner: [],
      pageIndex: 0,
      pageTotal: 0,
      pageSize: 10,
      selectedYear: 2019,
      selectedMonth: 'Jan',
      selectedMedal: '# of Medals',
      isAllTime: false,
      medalsLoading: false,
      months: [
        { name: 'Jan', number: 1 },
        { name: 'Feb', number: 2 },
        { name: 'Mar', number: 3 },
        { name: 'Apr', number: 4 },
        { name: 'May', number: 5 },
        { name: 'Jun', number: 6 },
        { name: 'Jul', number: 7 },
        { name: 'Aug', number: 8 },
        { name: 'Sept', number: 9 },
        { name: 'Oct', number: 10 },
        { name: 'Nov', number: 11 },
        { name: 'Dec', number: 12 }
      ],
      filteredmonths: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sept',
        'Oct',
        'Nov',
        'Dec'
      ]
    }
  },
  computed: {
    selectedMedalLabel () {
      const labels = {
        '# of Medals': 'Total medals',
        gold: 'Gold medals',
        silver: 'Silver medals',
        bronze: 'Bronze medals',
        live: 'Live medals'
      }
      return labels[this.selectedMedal] || 'Total medals'
    },
    activePeriodLabel () {
      if (this.selectedYear === 'AllTime') return 'All time'
      return `${this.selectedMonth} ${this.selectedYear}`
    }
  },
  methods: {
    async refreshMedals () {
      this.medalsLoading = true
      try {
        if (this.isRankedMedalSelected()) {
          await this.medalChangeHandle(false)
          return
        }

        const period = this.getPeriodArgs()
        await this.getAllMedals(period.year, period.month, this.pageIndex, this.pageSize)
      } finally {
        this.medalsLoading = false
      }
    },
    getPeriodArgs () {
      if (this.selectedYear === 'AllTime') {
        return { year: null, month: null }
      }

      const filteredMonth = this.getSelectedMonth()
      return {
        year: this.selectedYear,
        month: filteredMonth ? filteredMonth.number : null
      }
    },
    isRankedMedalSelected () {
      return ['gold', 'silver', 'bronze', 'live'].indexOf(this.selectedMedal) !== -1
    },
    async getAllMedals (year, month, index, size) {
      this.runner = []
      const [Gold, Silver, Bronze, Live] = await Promise.all([
        MedalService.getMedals(1, year, month, index, size),
        MedalService.getMedals(2, year, month, index, size),
        MedalService.getMedals(3, year, month, index, size),
        MedalService.getMedals(60, year, month, index, size)
      ])

      this.pageTotal = Gold.data.totalElements
      const RunnerGoldmedals = Gold.data.content
      const RunnerSilvermedals = Silver.data.content
      const RunnerBronzemedals = Bronze.data.content
      const RunnerLivemedals = Live.data.content

      RunnerGoldmedals.forEach(gold => {
        const runner = {}
        const silverObj = RunnerSilvermedals.filter(silver => silver.runner.id === gold.runner.id)
        const bronzeObj = RunnerBronzemedals.filter(bronze => bronze.runner.id === gold.runner.id)
        const liveObj = RunnerLivemedals.filter(live => live.runner.id === gold.runner.id)

        runner.silvercount = silverObj.length > 0 ? silverObj[0].count : 0
        runner.bronzecount = bronzeObj.length > 0 ? bronzeObj[0].count : 0
        runner.livecount = liveObj.length > 0 ? liveObj[0].count : 0
        runner.goldcount = gold.count
        runner.id = gold.runner.id
        runner.name = gold.runner.firstName
        runner.totalcount = runner.goldcount + runner.silvercount + runner.bronzecount + runner.livecount

        this.runner.push(runner)
      })

      this.tableData = this.runner
    },
    insertData (medal1, medal2, medal3, medal4) {
      this.runner = []
      medal1.forEach(defaultMedal => {
        const runner = {}
        const secondMedal = medal2.filter(second => second.runner.id === defaultMedal.runner.id)
        const thirdMedal = medal3.filter(third => third.runner.id === defaultMedal.runner.id)
        const fourthMedal = medal4.filter(fourth => fourth.runner.id === defaultMedal.runner.id)
        this.applyMedalCount(runner, defaultMedal)

        if (secondMedal.length > 0) {
          this.applyMedalCount(runner, secondMedal[0])
        }

        if (thirdMedal.length > 0) {
          this.applyMedalCount(runner, thirdMedal[0])
        }

        if (fourthMedal.length > 0) {
          this.applyMedalCount(runner, fourthMedal[0])
        }

        runner.goldcount = runner.goldcount || 0
        runner.silvercount = runner.silvercount || 0
        runner.bronzecount = runner.bronzecount || 0
        runner.livecount = runner.livecount || 0
        runner.id = defaultMedal.runner.id
        runner.name = defaultMedal.runner.firstName
        runner.totalcount = runner.goldcount + runner.silvercount + runner.bronzecount + runner.livecount

        this.runner.push(runner)
      })
      this.tableData = this.runner
    },
    applyMedalCount (runner, medalData) {
      if (medalData.medal.id === 1) {
        runner.goldcount = medalData.count
      } else if (medalData.medal.id === 2) {
        runner.silvercount = medalData.count
      } else if (medalData.medal.id === 3) {
        runner.bronzecount = medalData.count
      } else {
        runner.livecount = medalData.count
      }
    },
    pageChange (pageIndex) {
      this.pageIndex = pageIndex - 1
      this.refreshMedals()
    },
    pageSizeChange (pageSize) {
      this.pageIndex = 0
      this.pageSize = pageSize
      this.refreshMedals()
    },
    openRunnerData (runnerId) {
      const route = this.$router.resolve({
        name: 'runner-data',
        params: {
          runnerId
        }
      })
      window.open(route.href, '_blank')
    },
    yearChangeHandle () {
      this.pageIndex = 0
      this.isAllTime = this.selectedYear === 'AllTime'
      this.selectedMedal = '# of Medals'
      this.refreshMedals()
    },
    monthChangeHandle () {
      this.pageIndex = 0
      this.selectedMedal = '# of Medals'
      this.refreshMedals()
    },
    async medalChangeHandle (manageLoading = true) {
      if (manageLoading) {
        this.pageIndex = 0
        this.medalsLoading = true
      }

      try {
        this.isAllTime = this.selectedYear === 'AllTime'
        if (!this.isRankedMedalSelected()) {
          const period = this.getPeriodArgs()
          await this.getAllMedals(period.year, period.month, this.pageIndex, this.pageSize)
          return
        }

        const period = this.getPeriodArgs()
        const year = period.year
        const month = period.month

        if (this.selectedMedal === 'gold') {
          const [Gold, Silver, Bronze, Live] = await Promise.all([
            MedalService.getMedals(1, null, null, this.pageIndex, this.pageSize),
            MedalService.getMedals(2, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(3, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(60, year, month, this.pageIndex, this.pageSize)
          ])
          this.pageTotal = Gold.data.totalElements
          this.insertData(Gold.data.content, Silver.data.content, Bronze.data.content, Live.data.content)
        }

        if (this.selectedMedal === 'silver') {
          const [Gold, Silver, Bronze, Live] = await Promise.all([
            MedalService.getMedals(1, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(2, null, null, this.pageIndex, this.pageSize),
            MedalService.getMedals(3, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(60, year, month, this.pageIndex, this.pageSize)
          ])
          this.pageTotal = Silver.data.totalElements
          this.insertData(Silver.data.content, Gold.data.content, Bronze.data.content, Live.data.content)
        }

        if (this.selectedMedal === 'bronze') {
          const [Gold, Silver, Bronze, Live] = await Promise.all([
            MedalService.getMedals(1, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(2, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(3, null, null, this.pageIndex, this.pageSize),
            MedalService.getMedals(60, year, month, this.pageIndex, this.pageSize)
          ])
          this.pageTotal = Bronze.data.totalElements
          this.insertData(Bronze.data.content, Gold.data.content, Silver.data.content, Live.data.content)
        }

        if (this.selectedMedal === 'live') {
          const [Gold, Silver, Bronze, Live] = await Promise.all([
            MedalService.getMedals(1, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(2, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(3, year, month, this.pageIndex, this.pageSize),
            MedalService.getMedals(60, null, null, this.pageIndex, this.pageSize)
          ])
          this.pageTotal = Live.data.totalElements
          this.insertData(Live.data.content, Bronze.data.content, Gold.data.content, Silver.data.content)
        }
      } finally {
        if (manageLoading) {
          this.medalsLoading = false
        }
      }
    },
    getSelectedMonth () {
      return this.months.find(m => m.name === this.selectedMonth)
    }
  },
  created () {
    this.refreshMedals()
  },
  components: {
    Container
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.med-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.med-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.med-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
  margin-top: 2px;
}

.med-header-text {
  min-width: 0;
}

.med-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 3px;
  letter-spacing: 0;
}

.med-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  line-height: 1.45;
}

.med-summary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 11px;
  margin-left: auto;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  white-space: nowrap;
}

.med-summary-label {
  color: #8a8a9a;
  font-size: 12px;
}

.med-filter-grid {
  display: grid;
  grid-template-columns: minmax(150px, 0.65fr) minmax(150px, 0.65fr) minmax(220px, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

.med-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.med-label {
  font-size: 12px;
  color: #1a1a2e;
  font-weight: 600;
}

.med-select-wrap {
  position: relative;
  display: block;
  min-width: 0;
}

.med-select-wrap--disabled {
  opacity: 0.62;
}

.med-select {
  width: 100%;
  height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  padding: 0 34px 0 12px;
  outline: none;
  appearance: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.med-select:focus {
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.12);
}

.med-select:disabled {
  cursor: not-allowed;
}

.med-select-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  color: #8a8a9a;
  pointer-events: none;
  transform: translateY(-50%);
}

.med-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1rem;
}

.med-badge {
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  padding: 0 10px;
  border-radius: 999px;
  background: #5b4fcf;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.med-badge--soft {
  background: #f0effe;
  color: #413b94;
}

.med-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
}

.med-table {
  width: 100%;
  min-width: 880px;
  border: none;
  border-collapse: collapse;
  table-layout: fixed;
}

.med-table th {
  height: 42px;
  padding: 0 12px;
  background: #f7f7fb;
  border-bottom: 1px solid #e8e8ed;
  color: #1a1a2e;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
}

.med-table td {
  height: 40px;
  padding: 0 12px;
  border-bottom: 1px solid #f0f0f5;
  color: #3d3d52;
  font-size: 13px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.med-table th:first-child,
.med-table td:first-child {
  width: 260px;
  text-align: left;
}

.med-table tr:last-child td {
  border-bottom: none;
}

.med-table tbody tr {
  transition: background 0.15s;
}

.med-table tbody tr:hover {
  background: #fafafa;
}

.med-runner-link {
  color: #5b4fcf;
  font-weight: 700;
  text-decoration: none;
}

.med-runner-link:hover {
  color: #4338ca;
  text-decoration: underline;
}

.med-pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  overflow-x: auto;
}

.med-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 220px;
  border: 1px dashed #d8d8e5;
  border-radius: 10px;
  background: #fafafa;
  color: #8a8a9a;
  text-align: center;
}

.med-empty p {
  margin: 0;
  font-size: 13px;
}

.med-skeleton-table {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.med-sk-head,
.med-sk-row {
  display: grid;
  grid-template-columns: minmax(180px, 1.7fr) repeat(6, minmax(80px, 1fr));
  gap: 14px;
  padding: 13px 16px;
  border-bottom: 1px solid #f0f0f5;
}

.med-sk-row:last-child {
  border-bottom: none;
}

.med-sk-head {
  background: #fafafa;
}

.med-sk-cell {
  height: 14px;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

@media (max-width: 900px) {
  .med-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .med-field--wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 680px) {
  .pg-title-row,
  .med-header {
    align-items: flex-start;
  }

  .med-header {
    flex-wrap: wrap;
  }

  .med-summary {
    width: 100%;
    justify-content: space-between;
    margin-left: 48px;
  }

  .med-filter-grid {
    grid-template-columns: 1fr;
  }

  .med-card {
    padding: 1.25rem;
  }

  .med-pagination {
    justify-content: flex-start;
  }

  .med-sk-head,
  .med-sk-row {
    min-width: 760px;
  }

  .med-skeleton-table {
    overflow-x: auto;
  }
}
</style>



// WEBPACK FOOTER //
// Medal.vue?3329e8c6